// ============================================================================
// SUPABASE CLIENT CONFIGURATION
// ============================================================================

import { createClient } from '@supabase/supabase-js';

// Environment variables - set these in your .env file
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'YOUR_SUPABASE_URL';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'YOUR_SUPABASE_ANON_KEY';

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// ============================================================================
// AUTH FUNCTIONS
// ============================================================================

export const auth = {
  // Sign up with email and password
  async signUp(email, password, fullName) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName
        }
      }
    });
    return { data, error };
  },

  // Sign in with email and password
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  },

  // Sign in with Google
  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });
    return { data, error };
  },

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current user
  async getUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  // Get current session
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    return { session, error };
  },

  // Reset password
  async resetPassword(email) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`
    });
    return { data, error };
  },

  // Update password
  async updatePassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    });
    return { data, error };
  },

  // Listen to auth state changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  }
};

// ============================================================================
// PROFILE FUNCTIONS
// ============================================================================

export const profiles = {
  // Get user profile
  async get(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    return { data, error };
  },

  // Update user profile
  async update(userId, updates) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    return { data, error };
  },

  // Check subscription status
  async hasActiveSubscription(userId) {
    const { data, error } = await supabase
      .rpc('has_active_subscription', { user_uuid: userId });
    return { hasSubscription: data, error };
  }
};

// ============================================================================
// ASSESSMENT FUNCTIONS
// ============================================================================

export const assessments = {
  // Submit new assessment
  async submit(userId, assessmentData) {
    const { data, error } = await supabase
      .from('assessments')
      .insert({
        user_id: userId,
        ...assessmentData
      })
      .select()
      .single();
    return { data, error };
  },

  // Get user's assessment history
  async getHistory(userId, limit = 10) {
    const { data, error } = await supabase
      .from('assessments')
      .select('*')
      .eq('user_id', userId)
      .order('completed_at', { ascending: false })
      .limit(limit);
    return { data, error };
  },

  // Get single assessment
  async get(assessmentId) {
    const { data, error } = await supabase
      .from('assessments')
      .select('*')
      .eq('id', assessmentId)
      .single();
    return { data, error };
  },

  // Get latest assessment
  async getLatest(userId) {
    const { data, error } = await supabase
      .from('assessments')
      .select('*')
      .eq('user_id', userId)
      .order('completed_at', { ascending: false })
      .limit(1)
      .single();
    return { data, error };
  }
};

// ============================================================================
// REPORTS FUNCTIONS
// ============================================================================

export const reports = {
  // Create report for assessment
  async create(assessmentId, userId, reportType = 'full') {
    const { data, error } = await supabase
      .from('reports')
      .insert({
        assessment_id: assessmentId,
        user_id: userId,
        report_type: reportType,
        is_paid: reportType === 'preview' ? false : true
      })
      .select()
      .single();
    return { data, error };
  },

  // Get user's reports
  async getAll(userId) {
    const { data, error } = await supabase
      .from('reports')
      .select(`
        *,
        assessments (
          composite_score,
          risk_level,
          completed_at
        )
      `)
      .eq('user_id', userId)
      .order('generated_at', { ascending: false });
    return { data, error };
  },

  // Get single report
  async get(reportId) {
    const { data, error } = await supabase
      .from('reports')
      .select(`
        *,
        assessments (*)
      `)
      .eq('id', reportId)
      .single();
    return { data, error };
  },

  // Increment download count
  async incrementDownload(reportId) {
    const { data, error } = await supabase
      .rpc('increment_download_count', { report_id: reportId });
    return { data, error };
  }
};

// ============================================================================
// PROGRESS FUNCTIONS
// ============================================================================

export const progress = {
  // Get user's progress history
  async getHistory(userId, limit = 12) {
    const { data, error } = await supabase
      .from('progress_snapshots')
      .select('*')
      .eq('user_id', userId)
      .order('snapshot_date', { ascending: false })
      .limit(limit);
    return { data, error };
  },

  // Get dashboard summary
  async getDashboard(userId) {
    const { data, error } = await supabase
      .from('user_dashboard')
      .select('*')
      .eq('user_id', userId)
      .single();
    return { data, error };
  }
};

// ============================================================================
// PAYMENTS FUNCTIONS
// ============================================================================

export const payments = {
  // Create payment record
  async create(userId, paymentData) {
    const { data, error } = await supabase
      .from('payments')
      .insert({
        user_id: userId,
        ...paymentData
      })
      .select()
      .single();
    return { data, error };
  },

  // Get user's payment history
  async getHistory(userId) {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Update payment status
  async updateStatus(paymentId, status) {
    const { data, error } = await supabase
      .from('payments')
      .update({ status })
      .eq('id', paymentId)
      .select()
      .single();
    return { data, error };
  }
};

export default supabase;
